import type { KeypadInputProps } from './index.types';
import type { DTMFDigit } from 'Modules/softphone/constants';
import { DIGIT_GRID } from 'Modules/softphone/constants';
import { useSoftphoneKeypad } from 'Modules/softphone/hooks';
import React from 'react';
import { ALLOW_GLOBAL_SHORTCUTS_ATTRIBUTE } from 'Utils/isEditableTarget';
import { cn } from '~/lib/utils';

const validKeys = DIGIT_GRID.flat();

/**
 * KeypadInput Component
 * Displays the entered DTMF digits
 */
export const KeypadInput = ({
  testID,
  className,
  ...props
}: KeypadInputProps): React.ReactElement => {
  const { keypadDigits, handleDigitPress } = useSoftphoneKeypad();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.repeat) {
        event.preventDefault();
        return;
      }

      const key = event.key;
      if (validKeys.includes(key as DTMFDigit)) {
        event.preventDefault();
        handleDigitPress(key);
      }
    },
    [handleDigitPress]
  );

  // Auto-scroll and restore focus after digit changes
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
      // Restore focus to the input after a button click inserts a digit
      inputRef.current.focus();
    }
  }, [keypadDigits]);

  const keypadInputTestId = testID ? `${testID}-input` : undefined;

  return (
    <div
      className="w-full flex items-center justify-between gap-[8px] py-[4px] h-[22px] leading-[22px]"
      role="status"
      aria-live="polite"
      aria-label={`Entered digits: ${keypadDigits || 'none'}`}
      data-testid={`${keypadInputTestId}-container`}
    >
      <input
        ref={inputRef}
        type="text"
        value={keypadDigits || ''}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        autoFocus
        // Only DTMF digits are accepted here, so global softphone shortcuts
        // stay live while this input holds focus (see `isEditableTarget`) —
        // otherwise Alt+Shift+K could open the keypad but never close it.
        {...{ [ALLOW_GLOBAL_SHORTCUTS_ATTRIBUTE]: '' }}
        className={cn(
          'w-full min-w-0 ',
          'bg-transparent transition-colors outline-none',
          'text-[16px] font-normal text-foreground text-left tracking-wide',
          'placeholder-[#B3B3B3]',
          'selection:bg-primary selection:text-primary-foreground',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          'aria-invalid:border-destructive',
          className
        )}
        data-test-id={`${keypadInputTestId}-input-value`}
        aria-label="Entered DTMF digits"
        {...props}
      />
    </div>
  );
};
